import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNav from '@/components/AdminNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Send, CheckCircle2, XCircle, Users, AlertCircle, Briefcase, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '@/App';

const SubcontractorManagement = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [subcontractors, setSubcontractors] = useState([]);
  const [generalContractors, setGeneralContractors] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [sentInvitations, setSentInvitations] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [selectedCompanyToInvite, setSelectedCompanyToInvite] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role === 'company_admin') {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load subcontractors (for GC view)
      const subsResponse = await axiosInstance.get('/companies/my-subcontractors');
      setSubcontractors(subsResponse.data);

      // Load general contractors (for subcontractor view)
      const gcsResponse = await axiosInstance.get('/companies/my-general-contractors');
      setGeneralContractors(gcsResponse.data);

      // Load pending invitations
      const pendingResponse = await axiosInstance.get('/companies/pending-invitations');
      setPendingInvitations(pendingResponse.data);

      // Load sent invitations
      const sentResponse = await axiosInstance.get('/companies/sent-invitations');
      setSentInvitations(sentResponse.data);

      // Load all companies for invitation dropdown
      const companiesResponse = await axiosInstance.get('/companies/search?query=');
      setAllCompanies(companiesResponse.data.filter(c => c.id !== user.companyId));

    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load relationship data');
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitation = async () => {
    if (!selectedCompanyToInvite) {
      toast.error('Please select a company to invite');
      return;
    }

    try {
      await axiosInstance.post('/companies/invite-subcontractor', {
        subCompanyId: selectedCompanyToInvite
      });
      toast.success('Invitation sent successfully');
      setSelectedCompanyToInvite('');
      loadData();
    } catch (error) {
      console.error('Failed to send invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to send invitation');
    }
  };

  const handleAcceptInvitation = async (invitationId) => {
    try {
      await axiosInstance.post(`/companies/accept-invitation/${invitationId}`);
      toast.success('Invitation accepted! Relationship established.');
      loadData();
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      toast.error('Failed to accept invitation');
    }
  };

  const handleRejectInvitation = async (invitationId) => {
    try {
      await axiosInstance.post(`/companies/reject-invitation/${invitationId}`);
      toast.success('Invitation rejected');
      loadData();
    } catch (error) {
      console.error('Failed to reject invitation:', error);
      toast.error('Failed to reject invitation');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  if (user?.role !== 'company_admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <AdminNav user={user} onLogout={handleLogout} />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-gray-600">Only company admins can manage subcontractor relationships.</p>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav user={user} onLogout={handleLogout} />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">GC-Subcontractor Relationships</h1>
              <p className="text-gray-600 mt-1">Manage audit sharing relationships between companies</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/sub/my-projects')} variant="outline">
              <Building2 className="w-4 h-4 mr-2" />
              My GC Projects
            </Button>
            <Button onClick={() => navigate('/gc/dashboard')} className="bg-blue-600 hover:bg-blue-700">
              <Briefcase className="w-4 h-4 mr-2" />
              GC Project Dashboard
            </Button>
          </div>
        </div>

        {loading ? (
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-gray-600">Loading...</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Pending Invitations Received */}
            {pendingInvitations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                    Pending Invitations ({pendingInvitations.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingInvitations.map((invitation) => (
                      <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{invitation.gcCompanyName}</p>
                          <p className="text-sm text-gray-600">
                            Invited to establish audit-sharing relationship
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(invitation.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleAcceptInvitation(invitation.id)}
                            className="bg-green-600 hover:bg-green-700"
                            size="sm"
                          >
                            <CheckCircle2 className="w-4 h-4 mr-1" />
                            Accept
                          </Button>
                          <Button
                            onClick={() => handleRejectInvitation(invitation.id)}
                            variant="outline"
                            size="sm"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Invite New Subcontractor */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Send className="w-5 h-5" />
                  Invite Subcontractor
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <select
                    value={selectedCompanyToInvite}
                    onChange={(e) => setSelectedCompanyToInvite(e.target.value)}
                    className="flex-1 px-3 py-2 border rounded-lg"
                  >
                    <option value="">Select a company to invite...</option>
                    {allCompanies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                  <Button onClick={handleSendInvitation} disabled={!selectedCompanyToInvite}>
                    Send Invitation
                  </Button>
                </div>
                <p className="text-sm text-gray-600 mt-2">
                  Invite a subcontractor to establish an audit-sharing relationship. You'll be able to conduct audits on their sites.
                </p>
              </CardContent>
            </Card>

            {/* My Subcontractors */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  My Subcontractors ({subcontractors.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {subcontractors.length === 0 ? (
                  <p className="text-gray-600 text-center py-4">No subcontractor relationships established yet.</p>
                ) : (
                  <div className="space-y-3">
                    {subcontractors.map((relationship) => (
                      <div key={relationship.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{relationship.subCompanyName}</p>
                          <p className="text-sm text-gray-600">
                            Established on {new Date(relationship.establishedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge className="bg-green-100 text-green-800">Active</Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My General Contractors */}
            {generalContractors.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    General Contractors ({generalContractors.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {generalContractors.map((relationship) => (
                      <div key={relationship.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{relationship.gcCompanyName}</p>
                          <p className="text-sm text-gray-600">
                            Can conduct audits on your sites
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Established on {new Date(relationship.establishedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge className="bg-blue-100 text-blue-800">Active</Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sent Invitations */}
            {sentInvitations.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Send className="w-5 h-5 text-gray-600" />
                    Sent Invitations ({sentInvitations.filter(i => i.status === 'pending').length} pending)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {sentInvitations.map((invitation) => (
                      <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{invitation.subCompanyName}</p>
                          <p className="text-xs text-gray-500">
                            Sent {new Date(invitation.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                        <Badge 
                          className={
                            invitation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                            invitation.status === 'accepted' ? 'bg-green-100 text-green-800' :
                            'bg-red-100 text-red-800'
                          }
                        >
                          {invitation.status.charAt(0).toUpperCase() + invitation.status.slice(1)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default SubcontractorManagement;
